import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, X, RefreshCw } from 'lucide-react';

/**
 * Editor visual de slots de horario.
 *
 * Props:
 *   slots       – Array<{ inicio: string, fin: string }> | null
 *   onChange    – (newSlots: Array | null) => void
 *   label       – string (título de la sección)
 *   baseInicio  – number (hora inicio base, para auto-generar)
 *   baseFin     – number (hora fin base, para auto-generar)
 *   baseMinutos – number (slot_minutos base, para auto-generar)
 */
export const SlotEditor = ({ slots, onChange, label, baseInicio = 8, baseFin = 19, baseMinutos = 30 }) => {
  const list = slots || [];

  const handleChange = (idx, field, value) => {
    const updated = list.map((s, i) => i === idx ? { ...s, [field]: value } : s);
    onChange(updated.length ? updated : null);
  };

  const handleAdd = () => {
    const last = list[list.length - 1];
    const newInicio = last ? last.fin : `${String(baseInicio).padStart(2, '0')}:00`;
    const [h, m]    = newInicio.split(':').map(Number);
    const endMin    = h * 60 + m + baseMinutos;
    const newFin    = `${String(Math.floor(endMin / 60)).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
    onChange([...list, { inicio: newInicio, fin: newFin }]);
  };

  const handleRemove = (idx) => {
    const updated = list.filter((_, i) => i !== idx);
    onChange(updated.length ? updated : null);
  };

  const handleGenerate = () => {
    const generated = [];
    for (let min = baseInicio * 60; min < baseFin * 60; min += baseMinutos) {
      const h = Math.floor(min / 60);
      const m = min % 60;
      const endMin = min + baseMinutos;
      generated.push({
        inicio: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
        fin:    `${String(Math.floor(endMin / 60)).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`,
      });
    }
    onChange(generated);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleGenerate}
            className="h-7 px-2 text-xs text-muted-foreground"
            title="Generar automáticamente desde horario base"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Generar
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAdd}
            className="h-7 px-2 text-xs"
          >
            <Plus className="h-3 w-3 mr-1" />
            Agregar
          </Button>
        </div>
      </div>

      {list.length === 0 ? (
        <p className="text-xs text-muted-foreground italic px-1">
          Sin slots configurados — se usa horario automático ({baseInicio}:00–{baseFin}:00 cada {baseMinutos} min)
        </p>
      ) : (
        <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
          {list.map((slot, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input
                type="time"
                value={slot.inicio}
                onChange={e => handleChange(idx, 'inicio', e.target.value)}
                className="h-8 text-sm w-28 flex-shrink-0"
              />
              <span className="text-xs text-muted-foreground flex-shrink-0">→</span>
              <Input
                type="time"
                value={slot.fin}
                onChange={e => handleChange(idx, 'fin', e.target.value)}
                className="h-8 text-sm w-28 flex-shrink-0"
              />
              <button
                type="button"
                onClick={() => handleRemove(idx)}
                className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                title="Eliminar slot"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
